/**
 * Image Library
 *
 * Stores custom SVG definitions for icons not available in Lucide or FontAwesome.
 * Each SVG should have a viewBox and use currentColor for theming compatibility.
 */

import { logWarning, logError } from '../services/logging/log-handler.js';

export const imageLibrary = {
  "table-row-remove": {
    viewBox: "0 0 64 64",
    paths: ['<rect width="35" height="19" x="21" y="8" rx="3"/><path d="m0 24 16 16 m0 -16 -16 16"/><rect width="35" height="19" x="21" y="37" rx="3"/>'],
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3,
  },
  "table-column-remove": {
    viewBox: "0 0 64 64",
    paths: ['<path d="M16 8v48"/><path d="M16 51h37c3 0 5-3 5-5V19c0-3-3-5-5-5H16"/><path d="M48 8V3c0-3-3-5-5-5h-11c-3 0-5 3-5 5v5"/><line x1="29" x2="45" y1="27" y2="27"/><line x1="29" x2="45" y1="37" y2="37"/>'],
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3,
  },
  "table-row-add": {
    viewBox: "0 0 64 64",
    paths: ['<rect width="35" height="19" x="21" y="8" rx="3"/><path d="m5 24 8 8-8 8"/><rect width="35" height="19" x="21" y="37" rx="3"/>'],
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3,
  },
  "table-column-add": {
    viewBox: "0 0 64 64",
    paths: ['<rect width="19" height="35" x="8" y="21" rx="3"/><path d="m40 5-8 8-8-8"/><rect width="19" height="35" x="37" y="21" rx="3"/>'],
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3,
  },
  "table-pivot": {
    viewBox: "0 0 64 64",
    paths: ['<path d="M26 6H56C57.1046 6 58 6.89543 58 8V26H26V6Z"/><path d="M6 26H16H26L26 58H8C6.89543 58 6 57.1046 6 56L6 26Z"/><path d="M6 9C6 7.34315 7.34315 6 9 6H16H26L26 26H6L6 9Z"/><path d="M37 51H46C48.7614 51 51 48.7614 51 46V37"/><path d="M33 50.9992L37.5 45.803V56.1953L33 50.9992Z" fill="currentColor"/><path d="M51.0005 33.0003L56.1964 37.5006L45.8041 37.5L51.0005 33.0003Z" fill="currentColor"/>'],
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3,
  },
  "table-reset": {
    viewBox: "0 0 64 64",
    paths: ['<path d="M6 9C6 7.34314 7.34315 6 9 6H32H55C56.6569 6 58 7.34315 58 9L58 55C58 56.6569 56.6568 58 55 58H9C7.34314 58 6 56.6569 6 55L6 9Z"/><path d="M29.25 30.5625H16.75C16.3125 30.5625 16 30.1875 16 29.8125V17.25C16 16.875 16.3125 16.5 16.75 16.5H19.75C20.125 16.5 20.5 16.875 20.5 17.25V22.1875C23.3125 19 27.5 17 32.125 17.0625C40.6875 17.125 47.5 24 47.5 32.5625C47.5 41.125 40.5625 48 32 48C28 48 24.3125 46.5 21.5625 44C21.25 43.75 21.25 43.25 21.5625 42.9375L23.6875 40.8125C23.9375 40.5 24.375 40.5 24.6875 40.75C26.625 42.5 29.1875 43.5 32 43.5C38.0625 43.5 43 38.625 43 32.5C43 26.4375 38.0625 21.5 32 21.5C28.3125 21.5 25.125 23.3125 23.125 26.0625H29.25C29.625 26.0625 30 26.375 30 26.8125V29.8125C30 30.1875 29.625 30.5625 29.25 30.5625Z" fill="currentColor"/>'],
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 3,
  },
};

/**
 * Get SVG markup for a given image ID
 * @param id - The image ID from the library
 * @param size - Optional size for width/height attributes
 * @param color - Optional color override (defaults to currentColor)
 * @returns Complete SVG markup string
 */
export function getCustomSVG(id: string, size?: number, color?: string): string {
  const svgDef: any = (imageLibrary as any)[id];

  if (!svgDef) {
    logWarning(`Image ID "${id}" not found in image library`, "ImageLibrary", undefined);
    return "";
  }

  const width = size ? `${size}` : undefined;
  const height = size ? `${size}` : undefined;
  // Use fill from definition, only if explicitly set (undefined means respect element's own fill)
  const fillColor = svgDef.fill;
  // Use stroke from definition, only if explicitly set (undefined means respect element's own stroke)
  // If color override is provided, use that
  const strokeColor = color || svgDef.stroke;
  const strokeWidth = svgDef.strokeWidth;

  // Process each path string (which may contain multiple SVG elements)
  const paths = svgDef.paths
    .map((pathString: string) => {
      // Process each SVG element (path, line, rect, etc.) - handles self-closing tags
      return pathString.replace(/<([a-z]+)([^>]*?)(\/?)>/gi, (match, tagName, attributes, selfClose) => {
        let processedAttrs = (attributes || "").trim();

        // Handle fill - respect existing fill attribute unless color override is provided
        // If color is provided, override all fills. Otherwise, only apply default if missing.
        // Special case: if element already has fill="currentColor", preserve it (for filled arrowheads, etc.)
        if (color) {
          // Color override: replace all fills with the override color
          if (processedAttrs.includes("fill=")) {
            processedAttrs = processedAttrs.replace(/fill="[^"]*"/g, `fill="${color}"`);
          } else {
            processedAttrs = processedAttrs ? `fill="${color}" ${processedAttrs}` : `fill="${color}"`;
          }
        } else {
          // No color override: preserve existing fill if it's currentColor, otherwise apply default
          const existingFill = processedAttrs.match(/fill="([^"]*)"/);
          if (existingFill && existingFill[1] === "currentColor") {
            // Keep currentColor fill (for filled shapes like arrowheads)
          } else if (!processedAttrs.includes("fill=") && fillColor !== undefined) {
            processedAttrs = processedAttrs ? `fill="${fillColor}" ${processedAttrs}` : `fill="${fillColor}"`;
          }
        }

        // Handle stroke - respect existing stroke attribute unless color override is provided
        if (color) {
          // Color override: replace all strokes with the override color
          if (processedAttrs.includes("stroke=")) {
            processedAttrs = processedAttrs.replace(/stroke="[^"]*"/g, `stroke="${color}"`);
          } else {
            processedAttrs = processedAttrs ? `stroke="${color}" ${processedAttrs}` : `stroke="${color}"`;
          }
        } else {
          // No color override: only apply default stroke if element doesn't have one
          if (!processedAttrs.includes("stroke=") && strokeColor) {
            processedAttrs = processedAttrs ? `stroke="${strokeColor}" ${processedAttrs}` : `stroke="${strokeColor}"`;
          }
        }

        // Handle stroke-width - only apply if missing and not overridden by color
        if (!processedAttrs.includes("stroke-width=") && strokeWidth && processedAttrs.includes("stroke=")) {
          processedAttrs = processedAttrs ? `stroke-width="${strokeWidth}" ${processedAttrs}` : `stroke-width="${strokeWidth}"`;
        }

        // Reconstruct the element with processed attributes
        const closing = selfClose ? " />" : ">";
        return `<${tagName}${processedAttrs ? " " + processedAttrs : ""}${closing}`;
      });
    })
    .join("");

  const widthAttr = width ? ` width="${width}"` : "";
  const heightAttr = height ? ` height="${height}"` : "";

  return `<svg viewBox="${svgDef.viewBox}"${widthAttr}${heightAttr} fill="none" xmlns="http://www.w3.org/2000/svg">${paths}</svg>`;
}

/**
 * Get the SVG definition object for a given image ID
 * @param id - The image ID from the library
 * @returns SVG definition object or undefined
 */
export function getSVGDefinition(id: string): any {
  return (imageLibrary as any)[id];
}

/**
 * Check if an image ID exists in the library
 * @param id - The image ID to check
 * @returns true if the ID exists
 */
export function hasImage(id: string): boolean {
  return id in imageLibrary;
}

/**
 * Get all available image IDs
 * @returns Array of all image IDs in the library
 */
export function getAvailableImageIds(): string[] {
  return Object.keys(imageLibrary);
}
